// pages/Admin.js - Complete with all integrations
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import { useOrders } from '../hooks/useOrders';
import AdminSidebar from '../components/admin/AdminSidebar';
import Dashboard from '../components/admin/Dashboard';
import OrderManager from '../components/admin/OrderManager';
import ProductManager from '../components/admin/ProductManager';
import StaffManager from '../components/admin/StaffManager';
import AdminProfile from '../components/admin/AdminProfile';
import EnquiryManager from '../components/admin/EnquiryManager';
import BannerManager from '../components/admin/BannerManager';
import { Menu, X } from 'lucide-react';

const Admin = () => {
  const { user, isAdmin, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get products and product functions
  const { 
    products, 
    loading: productsLoading, 
    addProduct, 
    updateProduct, 
    deleteProduct,
    toggleProductStock
  } = useProducts();
  
  // Get orders and order functions
  const { 
    orders, 
    loading: ordersLoading, 
    confirmOrder, 
    processOrder, 
    dispatchOrder, 
    deliverOrder,
    verifyDeliveryOTP, 
    assignOrderToStaff, 
    rejectOrder, 
    staffList,
    getRevenueAnalytics
  } = useOrders();
  
  const navigate = useNavigate();

  // Protect route - redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, navigate]);

  // Handle window resize for mobile menu
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen]);

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        background: '#f8fafc',
        padding: '20px'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: '#fef2f2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '24px'
        }}>
          <span style={{ fontSize: '2.5rem' }}>🔒</span>
        </div>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px', textAlign: 'center' }}>Access Denied</h2>
        <p style={{ color: '#64748b', marginBottom: '24px', textAlign: 'center' }}>You don't have permission to access this area.</p>
        <button
          onClick={() => navigate('/admin/login')}
          style={{
            padding: '12px 24px',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.background = '#b91c1c'}
          onMouseLeave={(e) => e.target.style.background = '#dc2626'}
        >
          Go to Admin Login
        </button>
      </div>
    );
  }

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'banners', label: 'Banners', icon: '🎨' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'products', label: 'Products', icon: '🌸' },
    { id: 'enquiries', label: 'Decor Enquiries', icon: '💬' },
    { id: 'staff', label: 'Staff', icon: '👥' },
    { id: 'profile', label: 'Profile', icon: '👤' }
  ];

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard orders={orders} loading={ordersLoading} analytics={getRevenueAnalytics} />;
      case 'orders':
        return (
          <OrderManager 
            orders={orders} 
            loading={ordersLoading} 
            onConfirmOrder={confirmOrder}
            onProcessOrder={processOrder}
            onDispatchOrder={dispatchOrder}
            onDeliverOrder={deliverOrder}
            onVerifyOTP={verifyDeliveryOTP}
            onAssignOrder={assignOrderToStaff}
            onRejectOrder={rejectOrder}
            staffList={staffList}
            products={products}
          />
        );
      case 'banners':
        return <BannerManager />;
      case 'products':
        return (
          <ProductManager 
            products={products} 
            loading={productsLoading} 
            onAdd={addProduct} 
            onUpdate={updateProduct} 
            onDelete={deleteProduct}
            onToggleStock={toggleProductStock}
          />
        );
      case 'enquiries':
        return <EnquiryManager staffList={staffList} />;
      case 'staff':
        // IMPORTANT: Pass orders prop to StaffManager
        return <StaffManager staffList={staffList} onRefresh={() => {}} orders={orders} />;
      case 'profile':
        return <AdminProfile />;
      default:
        return <Dashboard orders={orders} loading={ordersLoading} analytics={getRevenueAnalytics} />;
    }
  };

  const currentMenuItem = menuItems.find(m => m.id === currentPage);

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: '#f8fafc',
      position: 'relative'
    }}>
      {/* Mobile Menu Toggle Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="mobile-menu-toggle"
        style={{
          position: 'fixed',
          top: '12px',
          left: '16px',
          zIndex: 1001,
          background: '#1e293b',
          border: 'none',
          width: '42px',
          height: '42px',
          borderRadius: '10px',
          display: 'none',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s'
        }}
      >
        {mobileMenuOpen ? <X size={20} color="white" /> : <Menu size={20} color="white" />}
      </button>

      {/* Sidebar */}
      <div className={`admin-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <AdminSidebar
          menuItems={menuItems}
          currentPage={currentPage}
          onPageChange={(page) => { 
            setCurrentPage(page); 
            setMobileMenuOpen(false);
          }}
          user={user}
          onLogout={() => { 
            logout(); 
            navigate('/admin/login');
          }}
        />
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999,
            animation: 'fadeIn 0.2s ease'
          }}
        />
      )}

      {/* Main Content */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <div className="header-title">
            <h1>{currentMenuItem?.label || 'Dashboard'}</h1>
            <p>Welcome back, {user?.name || 'Admin'}</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button 
              onClick={() => navigate('/')} 
              className="view-site-btn"
              style={{
                padding: '8px 16px',
                background: '#f1f5f9',
                color: '#334155',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f1f5f9';
              }}
            >
              <span>🏪</span> View Website
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="admin-content">
          {renderPage()}
        </div>
      </main>

      <style>{`
        @media (max-width: 768px) {
          .mobile-menu-toggle {
            display: flex !important;
          }
          .admin-sidebar {
            position: fixed;
            left: -280px;
            top: 0;
            bottom: 0;
            width: 280px;
            background: #0f172a;
            z-index: 1000;
            transition: left 0.3s ease-in-out;
            overflow-y: auto;
            box-shadow: 2px 0 10px rgba(0,0,0,0.1);
          }
          .admin-sidebar.open {
            left: 0;
          }
          .admin-main {
            margin-left: 0 !important;
            width: 100% !important;
            padding-top: 60px;
          }
          .admin-header {
            padding: 12px 16px !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .admin-header .header-title h1 {
            font-size: 1.2rem !important;
          }
          .admin-header .header-title p {
            font-size: 0.75rem !important;
          }
          .view-site-btn {
            padding: 6px 12px !important;
            font-size: 0.75rem !important;
            align-self: flex-start !important;
          }
          .admin-content {
            padding: 16px !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .admin-sidebar {
            width: 240px;
          }
          .admin-main {
            margin-left: 240px;
          }
          .admin-content {
            padding: 20px !important;
          }
        }
        @media (min-width: 1025px) {
          .admin-sidebar {
            width: 280px;
          }
          .admin-main {
            margin-left: 280px;
          }
          .admin-content {
            padding: 24px 30px !important;
          }
        }
        .admin-header {
          background: white;
          padding: 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e2e8f0;
          position: sticky;
          top: 0;
          z-index: 50;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        .header-title h1 {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
          color: #0f172a;
        }
        .header-title p {
          font-size: 0.8rem;
          color: #64748b;
          margin: 4px 0 0;
        }
        .admin-content {
          flex: 1;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .admin-sidebar::-webkit-scrollbar {
          width: 4px;
        }
        .admin-sidebar::-webkit-scrollbar-track {
          background: #1e293b;
        }
        .admin-sidebar::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 4px;
        }
        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Admin;